import React, { useMemo, useState } from "react";
import { geoAlbersUsa, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "@/assets/us-states-10m.json";

const W = 975;
const H = 610;

// Standard geoAlbersUsa fit for a 975×610 viewBox (handles AK/HI insets).
const projection = geoAlbersUsa().scale(1300).translate([W / 2, H / 2]);
const pathGen = geoPath(projection);

const statesGeo = feature(topo, topo.objects.states).features;

/**
 * Plots completed-shipment destinations as dots on a US map.
 * points: [{ lat, lng, count, city, state, zip }]
 */
export default function UsShipmentMap({ points = [] }) {
  const [hover, setHover] = useState(null);

  const projected = useMemo(() => {
    const out = [];
    let max = 1;
    for (const p of points) {
      const xy = projection([p.lng, p.lat]);
      if (!xy) continue; // outside US (territories) → skip
      max = Math.max(max, p.count || 1);
      out.push({ ...p, x: xy[0], y: xy[1] });
    }
    return { dots: out, max };
  }, [points]);

  const radius = (count) => {
    const min = 3.5, maxR = 16;
    const r = min + Math.sqrt(count / projected.max) * (maxR - min);
    return Math.min(maxR, r);
  };

  const totalShipped = points.reduce((a, p) => a + (p.count || 0), 0);

  return (
    <div data-testid="us-shipment-map" className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="US shipment destination map">
        {/* states */}
        {statesGeo.map((geo) => {
          const d = pathGen(geo);
          if (!d) return null;
          return (
            <path
              key={geo.id || geo.properties?.name}
              d={d}
              fill="#EAF0F6"
              stroke="#C3D0DD"
              strokeWidth={0.6}
            />
          );
        })}

        {/* destination dots */}
        {projected.dots.map((p) => (
          <circle
            key={p.zip}
            cx={p.x}
            cy={p.y}
            r={radius(p.count)}
            fill="#F9AA25"
            fillOpacity={0.55}
            stroke="#F9AA25"
            strokeWidth={1.1}
            className="cursor-pointer transition-[fill-opacity] duration-150 hover:fill-opacity-90"
            onMouseEnter={() => setHover({ ...p })}
            onMouseLeave={() => setHover(null)}
          />
        ))}
      </svg>

      {hover && (
        <div
          className="pointer-events-none absolute z-10 bg-bh-navy-deep text-white text-xs rounded-md px-2.5 py-1.5 shadow-lg"
          style={{
            left: `${(hover.x / W) * 100}%`,
            top: `${(hover.y / H) * 100}%`,
            transform: "translate(-50%, -130%)",
          }}
        >
          <div className="font-semibold">{hover.city ? `${hover.city}, ` : ""}{hover.state} {hover.zip}</div>
          <div className="text-bh-orange font-bold">{hover.count} shipment{hover.count === 1 ? "" : "s"}</div>
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-bh-muted mt-3 px-1">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-bh-orange/60 border border-bh-orange" /> Destination volume
        </span>
        <span className="text-bh-body font-semibold">
          {projected.dots.length} location{projected.dots.length === 1 ? "" : "s"} · {totalShipped.toLocaleString()} shipped
        </span>
      </div>
    </div>
  );
}
